import { CAMERA_OPACITY } from './visual/visualSettings'
import './styles.css'
import './performance.css'
import { HandMenu } from './gesture/handMenu'
import type { CommandRing, HandFrame, HandLandmark, Handedness, LoopLayerState, OverlayExtras, PinchCalibration, PinchState, VoicePreset } from './domain/types'
import { friendlyCameraError, startCamera, stopCamera, type CameraSession } from './camera/cameraService'
import { extendedFingerFlags } from './gesture/fingerCount'
import { COMMAND_LABELS, HandCommandDetector } from './gesture/handCommands'
import { LoopTapDetector } from './gesture/loopTap'
import { SongLesson } from './lesson/songLesson'
import { SongPreview } from './lesson/songPreview'
import { PinchAdapter, calibrationDiffers } from './gesture/pinchAdapt'
import { TemporalHandFilter } from './gesture/temporalFilter'
import { EchoGarden } from './music/echoGarden'
import { FirstRun } from './onboarding/firstRun'
import { DualHandMapper, LADDER_BOTTOM, LADDER_TOP, VOICE_REGISTERS, PENTATONIC_INTERVALS } from './music/thereminMapper'
import { createThereminSystem } from './music/thereminAudioEngine'
import { LocalCaptureController } from './recording/capture'
import { createVisionController, type VisionController } from './vision/visionController'
import { createCaptureCompositor } from './visual/captureCompositor'
import { createTrackingOverlay } from './visual/trackingOverlay'

const root = required<HTMLElement>('app')
const video = required<HTMLVideoElement>('camera-video')
const stage = required<HTMLCanvasElement>('tracking-overlay')
const captureStage = required<HTMLCanvasElement>('capture-stage')
const startCameraButton = required<HTMLButtonElement>('start-camera')
const startFallbackButton = required<HTMLButtonElement>('start-fallback')
const entryStatus = required<HTMLElement>('entry-status')
const loopTapButton = required<HTMLButtonElement>('loop-tap')
const playPauseButton = required<HTMLButtonElement>('play-pause')
const undoButton = required<HTMLButtonElement>('undo')
const clearAllButton = required<HTMLButtonElement>('clear-all')
const recordButton = required<HTMLButtonElement>('record')
const stopSessionButton = required<HTMLButtonElement>('stop-session')
const helpButton = required<HTMLButtonElement>('help')
const echoToggle = required<HTMLButtonElement>('echo-toggle')
const hushButton = required<HTMLButtonElement>('hush')
const advancedToggle = required<HTMLButtonElement>('advanced-toggle')
const echoCount = required<HTMLElement>('echo-count')
const masterVolume = required<HTMLInputElement>('master-volume')
const tutorialCard = required<HTMLElement>('tutorial-card')
const tutorialTitle = required<HTMLElement>('tutorial-title')
const tutorialCopy = required<HTMLElement>('tutorial-copy')
const tutorialSkip = required<HTMLButtonElement>('tutorial-skip')
const tutorialKicker = required<HTMLElement>('tutorial-kicker')
const lessonStart = required<HTMLButtonElement>('lesson-start')
const lessonSkip = required<HTMLButtonElement>('lesson-skip')
const status = required<HTMLElement>('status')
const noteReadout = required<HTMLElement>('note-readout')
const frequencyReadout = required<HTMLElement>('frequency-readout')
const takeDialog = required<HTMLDialogElement>('take-dialog')
const takeVideo = required<HTMLVideoElement>('take-video')
const takeAudio = required<HTMLAudioElement>('take-audio')
const takeCopy = required<HTMLElement>('take-copy')
const downloadTake = required<HTMLAnchorElement>('download-take')
const voiceButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-voice]')]

const silentAudio = import.meta.env.DEV && new URLSearchParams(location.search).get('testAudio') === '1'
const VOICE_PRESETS: VoicePreset[] = ['harp', 'music-box', 'piano', 'glass-bow', 'breath-choir', 'aurora']
let preset: VoicePreset = readPreset()
const mapper = new DualHandMapper(preset)
// Everything per-hand is keyed by spatial slot (0 = further right on screen), never by the left/right label.
const handFilter = new TemporalHandFilter()
const pinchDetectors: LoopTapDetector[] = (['right', 'left'] as Handedness[]).map((handedness) => new LoopTapDetector({ calibration: defaultCalibration(handedness), debounceMs: 220, minConfidence: 0.65, stableFrames: 3 }))
let pinchStates: PinchState[] = pinchDetectors.map((detector) => detector.lost())
const commandDetectors = [new HandCommandDetector(), new HandCommandDetector()]
let commandRings: CommandRing[] = []
let flash: string | null = null
let flashTimer = 0
let lesson: SongLesson | null = null
let lessonTapThisFrame = false
const garden = new EchoGarden({ bpm: 90, stepsPerBeat: 2, beatsPerCycle: 8, generations: 4, decay: 0.6, capacity: 32 })
let echoEnabled = readEchoEnabled()
let echoCursor = 0
let echoClock = 0
let advancedLayers = false

const pinchAdapters: PinchAdapter[] = (['right', 'left'] as Handedness[]).map((handedness) => new PinchAdapter(handedness))
let pinchAdaptFrames = 0
const firstRun = new FirstRun(readFirstRunComplete())

const overlay = createTrackingOverlay(stage, video, notes => system.audio.analyseOutput?.(notes))
video.style.setProperty('--camera-opacity',String(CAMERA_OPACITY))
let layers = emptyLayers()
const system = createThereminSystem(silentAudio, { onLayers: updateLayers, onAudioState })
const compositor = createCaptureCompositor(captureStage, video, stage, () => layers)
const capture = new LocalCaptureController(captureStage, () => system.audio.outputStream())

let currentFrame: HandFrame | null = null
let pointer = { x: 0.5, y: 0.55, gate: false }
let control = mapper.fallback(pointer.x, pointer.y, false, now())
let cameraSession: CameraSession | null = null
let vision: VisionController | null = null
let visionFrame = 0
let cameraGeneration = 0
let entering = false
let loopsPlaying = true
let recording = false
let captureGeneration = 0
let lastRecordingUrl: string | null = null
let hintDismissed = readHintDismissed()
let hintPinned = false
let lastHandReceivedAt = 0
let handWatchdog = 0
let controlsOpen = false
let cameraRequest:Promise<void>|null=null
let soundMuted=false
let soundGestureListening=false
let disarmSound:()=>void=()=>{}
let arrivalEnabled=true
let lastRawHandCount = 0
const captureTimers = new Set<number>()
let previewNote:number|null=null
const preview=new SongPreview(system.audio,()=>VOICE_REGISTERS[preset],note=>{previewNote=note;updateHintUi();renderOverlay()},()=>{required('lesson-preview').textContent=preview.playing?'Stop song':'Listen to song';required('lesson-preview-stop').hidden=!preview.playing})
required('lesson-direct').addEventListener('click',startLesson)
required('lesson-preview-stop').addEventListener('click',()=>preview.stop())
required('lesson-preview').addEventListener('click',()=>{
 if(preview.playing){preview.stop();return}
 lesson=null;setPerformanceControls(false);hintPinned=true;system.audio.setMasterLevel(Number(masterVolume.value)/100)
 void preview.start().then(updateSoundUi).catch(error=>announce(error instanceof Error?error.message:'Sound could not start for the song.'))
 updateHintUi()
})
const handMenu=new HandMenu({video,stage,panel:required('performance-controls'),isOpen:()=>controlsOpen,isModal:()=>takeDialog.open,isGuide:()=>hintPinned,toggle:()=>{if(takeDialog.open)takeDialog.close();else setPerformanceControls(!controlsOpen)}})
const cameraMessage=required('camera-message')
const soundToggle=required<HTMLButtonElement>('sound-toggle')
const retryCamera=required<HTMLButtonElement>('retry-camera')

root.dataset.camera = 'false'
root.dataset.advanced = 'false'
root.dataset.fluid = 'false'
masterVolume.value = String(Math.round(readMasterLevel() * 100))
system.audio.setMasterLevel(Number(masterVolume.value) / 100)
// The engine follows the remembered voice from the first moment, so a note or an
// echo can never come out in a different instrument than the one shown.
void system.audio.setPreset(preset).catch(() => { entryStatus.textContent = 'The instrument sounds will finish loading when you start.' })
// Sound starts on arrival wherever the browser allows it; a browser that insists on
// a gesture first gets one from the first tap, click or key anywhere. Nothing is asked.
armSound()
updateVoiceUi()
updateSoundUi()
updateEchoUi()
updateLayers(layers, 'ready')
updateHintUi()
renderOverlay()
const probeMode = import.meta.env.DEV && new URLSearchParams(location.search).get('probe') === '1'
if (silentAudio || probeMode) (window as Window & { __aetherInjectFrame?: (frame: HandFrame) => void }).__aetherInjectFrame = processHandFrame
if (probeMode) {
  // Dev-only inspection of what the tracker and mapper are doing, for headless verification.
  ;(window as Window & { __aetherState?: () => unknown }).__aetherState = () => ({
    audioVisual: system.audio.analyseOutput?.(PENTATONIC_INTERVALS.map(interval=>VOICE_REGISTERS[preset]+interval)),
    rawHands: lastRawHandCount,
    hands: (currentFrame?.hands ?? []).map((hand) => ({ slot: hand.slot, handedness: hand.handedness, confidence: hand.confidence, tip: hand.landmarks[8] })),
    voices: control.voices.map((voice) => ({ slot: voice.slot, active: voice.active, gate: voice.gate, noteIndex: voice.noteIndex, midi: voice.targetMidi, level: Number(voice.level.toFixed(3)) })),
    lastEvents: control.events.map((event) => `${event.type}:${event.midi}`),
    echoes: garden.size,
    camera: root.dataset.camera
  })
}

startCameraButton.addEventListener('click', () => void enterInstrument(true))
startFallbackButton.addEventListener('click', () => void enterInstrument(false))
loopTapButton.addEventListener('click', () => triggerLoopTap())
playPauseButton.addEventListener('click', () => void toggleLoops())
undoButton.addEventListener('click', () => { system.looper.undo(); announce('Last completed layer undone.') })
clearAllButton.addEventListener('click', () => { system.looper.clearAll(); announce('All four layers cleared. Layer 1 will define a new phrase.') })
recordButton.addEventListener('click', beginCapture)
stopSessionButton.addEventListener('click', exitInstrument)
helpButton.addEventListener('click', replayGuide)
tutorialSkip.addEventListener('click', () => { if (lesson) endLesson(); else skipGuide() })
lessonStart.addEventListener('click', startLesson)
lessonSkip.addEventListener('click', () => { if (lesson?.skipStep()) { system.audio.playUiCue(); updateHintUi(); renderOverlay() } })
echoToggle.addEventListener('click', () => setEcho(!echoEnabled))
hushButton.addEventListener('click', () => hushGarden(true))
advancedToggle.addEventListener('click', () => setAdvancedLayers(!advancedLayers))
masterVolume.addEventListener('input', () => {
  const level = Number(masterVolume.value) / 100
  system.audio.setMasterLevel(level)
  try { localStorage.setItem('aether-master-level-v3', String(level)) } catch { /* optional */ }
})
voiceButtons.forEach((button) => button.addEventListener('click', () => void selectPreset(button.dataset.voice as VoicePreset)))

document.querySelectorAll<HTMLButtonElement>('[data-layer-action]').forEach((button) => {
  button.addEventListener('click', () => {
    const layer = Number(button.dataset.layerIndex)
    if (button.dataset.layerAction === 'mute') {
      const unmuting = layers[layer]?.status === 'muted'
      system.looper.toggleMute(layer)
      announce(`Layer ${layer + 1} ${unmuting ? 'unmuted' : 'muted'}.`)
    } else {
      system.looper.clear(layer)
      announce(layer === 0 ? 'Layer 1 cleared. The full phrase session was reset.' : `Layer ${layer + 1} cleared.`)
    }
  })
})

stage.addEventListener('pointerdown', (event) => {
  if (controlsOpen || takeDialog.open || event.button !== 0) return
  pointer = { ...pointFromEvent(event), gate: true }
  stage.setPointerCapture?.(event.pointerId)
  updateFallbackControl()
})
stage.addEventListener('pointermove', (event) => {
  if (controlsOpen || takeDialog.open || !pointer.gate) return
  const next = pointFromEvent(event)
  
  pointer = { ...next, gate: true }
  updateFallbackControl()
})
stage.addEventListener('pointerup', releasePointer)
stage.addEventListener('pointercancel', releasePointer)
stage.addEventListener('keydown', onStageKeyDown)
window.addEventListener('keydown', onGlobalKeyDown)
required('reveal-controls').addEventListener('click', () => setPerformanceControls(true))
required('hide-controls').addEventListener('click', () => setPerformanceControls(false))
takeDialog.addEventListener('close', () => { takeVideo.pause(); takeAudio.pause(); resetTrackingState(); stage.focus() })
soundToggle.addEventListener('click',toggleSound)
retryCamera.addEventListener('click',()=>{arrivalEnabled=true;void ensureCamera()})
for(const id of ['hand-scroll-up','hand-scroll-down'])required(id).addEventListener('click',()=>{required('performance-controls').scrollTop+=Number(required(id).dataset.handScroll)*180})
required('replay-take').addEventListener('click',()=>{const media=takeVideo.hidden?takeAudio:takeVideo;if(media.paused)void media.play();else media.pause()})
window.addEventListener('resize', () => overlay.resize())
window.addEventListener('pagehide', onPageHide)
window.addEventListener('pageshow', onPageShow)
document.addEventListener('visibilitychange', onVisibilityChange)

function ensureCamera():Promise<void>{
  if(cameraSession)return Promise.resolve()
  if(cameraRequest)return cameraRequest
  cameraRequest=bootCamera(++cameraGeneration).finally(()=>{cameraRequest=null})
  return cameraRequest
}

async function enterInstrument(useCamera: boolean): Promise<void> {
  if (entering) return
  entering = true
  if(useCamera){arrivalEnabled=true;void ensureCamera()}
  else {arrivalEnabled=false;releaseCamera()}
  startCameraButton.disabled = true
  startFallbackButton.disabled = true
  try {
    entryStatus.textContent = 'Waking the instrument…'
    // The click that brought us here is the gesture a strict browser wants before sound.
    await system.audio.unlock()
    await system.audio.setPreset(preset)
    system.audio.setMasterLevel(Number(masterVolume.value) / 100)
    entryStatus.textContent = ''
    enterPlayMode()
    if (useCamera) await ensureCamera()
    else {
      root.dataset.camera = 'false'
      announce('Camera-free instrument live. Drag up for higher notes, right for brighter tone, and press Space to loop.')
    }
    updateHintUi()
    stage.focus()
  } catch (error) {
    const message = error instanceof Error ? `Audio could not start: ${error.message}` : 'Audio could not start in this browser.'
    entryStatus.textContent = `${message} Try a current Chrome, Edge, Firefox, or Safari release.`
  } finally {
    entering = false; startCameraButton.disabled = false; startFallbackButton.disabled = false
  }
}

// One way into the performance for every entry path (camera arrival, the two
// start buttons): stage, hand watchdog, echo clock and sound all come up together.
function enterPlayMode(): void {
  root.dataset.mode = 'play'
  hintPinned = false
  stage.dataset.chrome = 'hidden'
  startHandWatchdog()
  setPerformanceControls(false)
  if (!loopsPlaying) { loopsPlaying = true; system.looper.setPlaying(true); updatePlayPauseUi() }
  overlay.resize()
  startEchoClock()
  updateLayers(layers, 'tick')
  updateSoundUi()
  void wakeSound()
}

// Sound starts by itself; the button only turns it off (and back on).
function armSound(): void {
  void wakeSound()
  listenForSoundGesture()
}

function listenForSoundGesture(): void {
  if (soundGestureListening) return
  soundGestureListening = true
  const onGesture = (): void => { void wakeSound() }
  const types = ['pointerdown', 'pointerup', 'touchend', 'keydown', 'click'] as const
  for (const type of types) window.addEventListener(type, onGesture, { capture: true, passive: true })
  disarmSound = () => {
    for (const type of types) window.removeEventListener(type, onGesture, { capture: true })
    soundGestureListening = false
    disarmSound = () => {}
  }
}

async function wakeSound(): Promise<void> {
  if (soundMuted) return
  try { await system.audio.unlock() } catch (error) {
    announce(error instanceof Error ? `Audio could not start: ${error.message}` : 'Audio could not start in this browser.')
    return
  }
  system.audio.setMasterLevel(Number(masterVolume.value) / 100)
  updateSoundUi()
}

function onAudioState(running: boolean): void {
  if (running) disarmSound()
  else listenForSoundGesture()
  updateSoundUi()
}

function toggleSound(): void {
  if (system.audio.running && !soundMuted) {
    soundMuted = true
    system.audio.setMuted(true)
    announce('Sound off. The instrument keeps tracking; press Sound on to hear it again.')
  } else {
    soundMuted = false
    system.audio.setMuted(false)
    void wakeSound()
    announce('Sound on.')
  }
  updateSoundUi()
}

function updateSoundUi(): void {
  const audible = system.audio.running && !soundMuted
  soundToggle.hidden = root.dataset.mode !== 'play'
  soundToggle.textContent = audible ? 'Sound off' : 'Sound on'
  soundToggle.setAttribute('aria-pressed', String(audible))
  soundToggle.dataset.state = soundMuted ? 'muted' : system.audio.running ? 'on' : 'waiting'
  soundToggle.title = audible ? 'Turn the music off' : soundMuted ? 'Turn the music back on' : 'Start the music'
}

async function bootCamera(generation: number): Promise<void> {
  announce('Requesting camera permission…')
  try {
    const session = await startCamera(video, 'user')
    if (generation !== cameraGeneration || !arrivalEnabled) { stopCamera(session); return }
    cameraSession = session
    if (root.dataset.mode !== 'play') enterPlayMode()
    retryCamera.hidden=true;cameraMessage.textContent=''
    root.dataset.camera = 'true'
    overlay.resize()
    
    const controller = await createVisionController({ onFrame: (frame)=>{root.dataset.visionFrames=String(Number(root.dataset.visionFrames||0)+1);processHandFrame(frame)}, onStatus: (message)=>{root.dataset.visionStatus=message;announce(message.replace(/ · (GPU|CPU|mobile)$/,""))}, onError: handleTrackingError })
    if (generation !== cameraGeneration || root.dataset.mode !== 'play') { controller.dispose(); return }
    vision = controller
    videoFrame()
    updateHintUi()
    announce('Each hand is a voice. Point your index finger higher for higher notes, make a fist to rest, push toward the camera to swell. Hold two fingers up to change voice, thumbs-up for echo. Enable Layers before pinching to loop.')
    session.stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      cancelActiveLoopRecording(); releaseCamera(); system.audio.release()
      retryCamera.hidden=false;cameraMessage.textContent='Camera disconnected. Reconnect it, then allow camera again.'
      announce('Camera stopped. Pointer, touch, and keyboard controls remain live.')
    }, { once: true })
  } catch (error) {
    if(generation!==cameraGeneration||!arrivalEnabled)return
    releaseCamera()
    cameraMessage.textContent=friendlyCameraError(error);retryCamera.hidden=false
    announce(friendlyCameraError(error))
  }
}

function processHandFrame(rawFrame: HandFrame): void {
  if (root.dataset.mode !== 'play') return
  lastHandReceivedAt = now()
  const menuOwnsFrame=handMenu.push(rawFrame)
  root.dataset.menuFrames=String(handMenu.trace.frames)
  root.dataset.menuHands=String(handMenu.trace.visibleHands)
  root.dataset.menuOpens=String(handMenu.trace.opens)
  if (menuOwnsFrame || controlsOpen || takeDialog.open) { resetTrackingState(); return }
  // Deliberate pointer/keyboard playing owns the gate until it is released.
  if (pointer.gate) return
  lastRawHandCount = rawFrame.hands.length
  const frame = handFilter.push(rawFrame)
  currentFrame = frame
  const frozen = new Set<number>()
  commandRings = []
  lessonTapThisFrame = false
  for (let slot = 0; slot < pinchDetectors.length; slot += 1) {
    const hand = frame.hands.find((candidate) => candidate.slot === slot && candidate.confidence >= 0.5 && candidate.landmarks.length >= 21) ?? null
    if (!hand) {
      pinchStates[slot] = pinchDetectors[slot]!.lost()
      commandDetectors[slot]!.push(null, frame.timestampMs)
      continue
    }
    const state = pinchDetectors[slot]!.push({
      distanceRatio: normalizedPinchDistance(hand.landmarks),
      confidence: hand.confidence,
      timestampMs: frame.timestampMs
    })
    pinchStates[slot] = state
    const pinching = state.state === 'contact' || state.state === 'held' || state.state === 'accepted'
    if (pinching) frozen.add(slot)
    pinchAdapters[slot]!.observe(normalizedPinchDistance(hand.landmarks))
    if (!pinching && pinchAdaptFrames % 45 === 0) {
      const learned = pinchAdapters[slot]!.calibration()
      if (calibrationDiffers(pinchDetectors[slot]!.currentCalibration ?? learned, learned)) pinchDetectors[slot]!.setCalibration(learned)
    }
    // A quick pinch is a loop command on either hand; a held pinch hushes the echoes.
    if (state.tap && advancedLayers) { lessonTapThisFrame = true; system.audio.playUiCue(); triggerLoopTap(frame.timestampMs, false) }
    if (state.hold) hushGarden(true)

    // Held poses are commands: a ring fills at the fingertip, then it fires once.
    const command = commandDetectors[slot]!.push(pinching ? null : extendedFingerFlags(hand), frame.timestampMs)
    if (command.command) {
      frozen.add(slot)
      commandRings.push({ slot, label: COMMAND_LABELS[command.command], progress: command.progress })
    }
    if (command.fired === 'next-voice') void cycleVoice()
    else if (command.fired === 'toggle-echo') setEcho(!echoEnabled)
  }
  pinchAdaptFrames += 1
  control = mapper.update(frame.hands, frame.timestampMs, { frozen, ladder: visibleLadderRange() })
  system.audio.update(control)
  plantEchoes()
  observeFirstRun(frame)
  observeLesson(frame.hands.length > 0, frame.timestampMs)
  updateNoteReadout()
  renderOverlay()
}

function observeLesson(handVisible: boolean, timestampMs: number): void {
  if (!lesson) return
  const playing = control.voices.find((voice) => voice.gate)
  const changed = lesson.observe({ handVisible, playing: Boolean(playing), noteIndex: playing?.noteIndex ?? null, level: playing?.level ?? 0, loopTap: lessonTapThisFrame }, timestampMs)
  if (!changed) return
  if (lesson.view.complete) { announce('You played the whole song. Point, open, push, pinch — that is the instrument.'); showFlash('You played it.') }
  else system.audio.playUiCue()
  updateHintUi()
}

function startLesson(): void {
  preview.stop()
  void wakeSound()
  setPerformanceControls(false)
  lesson = new SongLesson()
  hintPinned = true
  announce('Lesson started: Mary Had a Little Lamb. Follow the glowing line at your own pace.')
  updateHintUi()
  renderOverlay()
}

function endLesson(): void {
  lesson = null
  // Ending a lesson also clears its teaching overlay.
  hintPinned = false
  updateHintUi()
  renderOverlay()
  stage.focus()
  announce('Lesson ended. Play freely.')
}

async function cycleVoice(): Promise<void> {
  const next = VOICE_PRESETS[(VOICE_PRESETS.indexOf(preset) + 1) % VOICE_PRESETS.length]!
  showFlash(voiceLabel(next))
  await selectPreset(next)
}

function setEcho(enabled: boolean): void {
  echoEnabled = enabled
  try { localStorage.setItem('aether-echo-v5', echoEnabled ? 'on' : 'off') } catch { /* optional */ }
  if (!echoEnabled) hushGarden(false)
  updateEchoUi()
  showFlash(echoEnabled ? 'Echo on' : 'Echo off')
  announce(echoEnabled ? 'Echo on. Every note you play will repeat and slowly fade.' : 'Echo off. Notes ring only while you play them.')
}

function showFlash(text: string): void {
  flash = text
  window.clearTimeout(flashTimer)
  flashTimer = window.setTimeout(() => { flash = null; renderOverlay() }, 1_500)
  renderOverlay()
}

function overlayExtras(): OverlayExtras {
  const target = lesson?.target ?? null
  return { commands: commandRings, lesson: previewNote!==null ? { noteIndex:previewNote,label:'Listen · Mary Had a Little Lamb' } : lesson && target !== null ? { noteIndex: target, label: lesson.view.step.lyric } : null, flash }
}

function observeFirstRun(frame: HandFrame): void {
  if (firstRun.isComplete()) return
  const playing = control.voices.find((voice) => voice.gate)
  const changed = firstRun.observe({ handVisible: frame.hands.length > 0, playing: Boolean(playing), noteIndex: playing?.noteIndex ?? null }, frame.timestampMs)
  if (!changed) return
  if (firstRun.isComplete()) { saveFirstRunComplete(); announce('You have it. Everything else is optional.') }
  updateHintUi()
}

function plantEchoes(): void {
  // Only notes that were actually heard grow echoes, and each one remembers its voice.
  if (!echoEnabled || !system.audio.running || soundMuted) return
  const now = system.audio.currentTime
  for (const event of control.events) {
    if (event.type === 'off') continue
    garden.add({ slot: event.slot, midi: event.midi, velocity: event.velocity, timbre: event.timbre, pan: event.pan, preset }, now)
  }
  if (control.events.length > 0) updateEchoUi()
}

function startEchoClock(): void {
  window.clearInterval(echoClock)
  echoCursor = system.audio.currentTime
  echoClock = window.setInterval(() => {
    const now = system.audio.currentTime
    const from = Math.max(echoCursor, now)
    const to = now + 0.22
    if (to <= from) return
    const before = garden.size
    for (const fire of garden.collect(from, to)) system.audio.playEcho(fire)
    echoCursor = to
    if (garden.size !== before) updateEchoUi()
  }, 70)
}

function stopEchoClock(): void {
  window.clearInterval(echoClock)
  echoClock = 0
}

function hushGarden(announceIt: boolean): void {
  if (garden.size === 0 && !announceIt) return
  garden.clear()
  system.audio.hush()
  updateEchoUi()
  if (announceIt) announce('Hushed. The echoes are cleared.')
}

function setAdvancedLayers(enabled: boolean): void {
  advancedLayers = enabled
  root.dataset.advanced = String(enabled)
  advancedToggle.setAttribute('aria-pressed', String(enabled))
  advancedToggle.textContent = enabled ? 'Hide layers' : 'Layers'
  if (enabled) { overlay.resize(); updateLayers(layers, 'tick') }
}

function updateEchoUi(): void {
  echoToggle.setAttribute('aria-pressed', String(echoEnabled))
  echoToggle.classList.toggle('is-selected', echoEnabled)
  echoCount.textContent = echoEnabled ? (garden.size > 0 ? `${garden.size} echo${garden.size === 1 ? '' : 'es'}` : 'echo ready') : 'echo off'
  hushButton.disabled = garden.size === 0
}

// With object-fit: cover the camera frame's top and bottom can be cropped away;
// keep the note ladder inside the band the player can actually see.
function visibleLadderRange(): { top: number; bottom: number } {
  const videoWidth = video.videoWidth || 16
  const videoHeight = video.videoHeight || 9
  const stageWidth = Math.max(1, stage.clientWidth)
  const stageHeight = Math.max(1, stage.clientHeight)
  const scale = Math.max(stageWidth / videoWidth, stageHeight / videoHeight)
  const renderedHeight = videoHeight * scale
  const hidden = Math.max(0, (renderedHeight - stageHeight) / 2 / renderedHeight)
  return { top: Math.max(LADDER_TOP, hidden + 0.06), bottom: Math.min(LADDER_BOTTOM, 1 - hidden - 0.06) }
}

function triggerLoopTap(sourceTimestampMs = now(), cue = true): void {
  if (!loopsPlaying) { announce('Loops are paused. Press Play before recording a layer.'); return }
  if (!advancedLayers) setAdvancedLayers(true)
  if (cue) { lessonTapThisFrame = true; system.audio.playUiCue() }
  system.looper.tap(sourceTimestampMs)
  if (lesson && root.dataset.camera !== 'true') observeLesson(true, now())
}

function updateLayers(nextLayers: readonly LoopLayerState[], feedback: string): void {
  layers = nextLayers.map((layer, index) => ({ ...layer, waveformPeaks: layer.waveformPeaks.length > 0 ? [...layer.waveformPeaks] : [...(layers[index]?.waveformPeaks ?? [])] }))
  playPauseButton.disabled = layers.some((layer) => layer.status === 'recording')
  const activeIndex = layers.findIndex((layer) => layer.status === 'empty' || layer.status === 'recording')
  layers.forEach((layer) => {
    const strip = document.querySelector<HTMLElement>(`[data-layer="${layer.index}"]`)
    if (!strip) return
    strip.dataset.status = layer.status
    strip.classList.toggle('is-active', layer.index === activeIndex || layer.status === 'recording')
    const state = strip.querySelector<HTMLElement>('[data-layer-state]')
    if (state) { state.setAttribute('role', 'status'); state.setAttribute('aria-live', 'polite'); state.textContent = layerLabel(layer) }
    const progress = strip.querySelector<HTMLElement>('[data-layer-progress]')
    if (progress) progress.style.width = `${layer.loopPhase * 100}%`
    const mute = strip.querySelector<HTMLButtonElement>('[data-layer-action="mute"]')
    const clear = strip.querySelector<HTMLButtonElement>('[data-layer-action="clear"]')
    const actionable = layer.status !== 'empty' && layer.status !== 'recording'
    if (mute) {
      const muted = layer.status === 'muted'
      mute.disabled = !actionable; mute.textContent = muted ? 'Unmute' : 'Mute'; mute.setAttribute('aria-pressed', String(muted)); mute.setAttribute('aria-label', `${muted ? 'Unmute' : 'Mute'} layer ${layer.index + 1}`)
    }
    if (clear) clear.disabled = layer.status === 'empty'
    drawWaveform(required<HTMLCanvasElement>(`layer-wave-${layer.index}`), layer)
  })
  if (feedback !== 'tick' && feedback !== 'ready') announceFeedback(feedback)
  if (!hintDismissed && layers[0]?.status === 'playing') {
    hintDismissed = true
    saveHintDismissed()
    hintPinned = false
    updateHintUi()
  }
  renderOverlay()
}

function announceFeedback(feedback: string): void {
  const messages: Record<string, string> = {
    'record-started': layers[0]?.status === 'recording' ? 'Layer 1 recording. Play a phrase, then pinch again after at least 0.8 seconds.' : `Layer ${layers.findIndex((layer) => layer.status === 'recording') + 1} recording in phase. Pinch again to close it.`,
    'too-short': 'Keep playing. Layer 1 must be at least 0.8 seconds long.',
    'record-closed': 'Layer closed and playing. Change voice or move somewhere new, then pinch to add the next layer.',
    'record-auto-closed': 'Layer completed one full cycle and is now playing.', full: '4 layers full. Mute or clear a layer to make room.',
    clearAll: 'All layers cleared. Layer 1 will define a new phrase.', undo: 'Last completed layer undone.'
  }
  const message = messages[feedback]
  if (message) announce(message)
}

async function selectPreset(next: VoicePreset): Promise<void> {
  if (!VOICE_PRESETS.includes(next)) return
  preset = next
  mapper.setPreset(next)
  updateVoiceUi()
  try { localStorage.setItem('aether-voice-v5', next) } catch { /* optional */ }
  announce(`Loading ${voiceLabel(next)}…`)
  try {
    await system.audio.setPreset(next)
  } catch (error) {
    announce(error instanceof Error ? error.message : `${voiceLabel(next)} could not load.`)
    return
  }
  if (preset !== next) return
  if (root.dataset.camera !== 'true') updateFallbackControl()
  announce(`${voiceLabel(next)} ready. Echoes and loop layers keep their recorded sound.`)
}

function updateVoiceUi(): void {
  voiceButtons.forEach((button) => {
    const selected = button.dataset.voice === preset
    button.setAttribute('aria-pressed', String(selected)); button.classList.toggle('is-selected', selected)
  })
}

function updateHintUi(): void {
  const show = root.dataset.mode === 'play' && hintPinned
  stage.dataset.chrome = show ? 'guided' : 'hidden'
  if (!show && tutorialCard.contains(document.activeElement)) stage.focus()
  tutorialCard.hidden = !show
  tutorialSkip.hidden = false
  tutorialSkip.textContent = lesson ? 'End lesson' : 'Hide guide'
  lessonStart.hidden = Boolean(lesson)
  lessonSkip.hidden = !lesson || Boolean(lesson?.view.complete)
  const reference=tutorialCard.querySelector<HTMLElement>('.gesture-reference');if(reference)reference.hidden=Boolean(lesson)||preview.playing
  if (!show) return
  if(preview.playing){tutorialKicker.textContent='LISTEN';tutorialTitle.textContent='Mary Had a Little Lamb';tutorialCopy.textContent='Hear the song, then choose Learn song to follow the colored notes at your own pace.';return}
  if (lesson) {
    const view = lesson.view
    tutorialKicker.textContent = view.complete ? 'LESSON COMPLETE' : view.noteNumber !== null ? `NOTE ${view.noteNumber} OF ${view.noteTotal}` : view.step.kind.toUpperCase()
    tutorialTitle.textContent = view.complete ? view.step.lyric : view.title
    tutorialCopy.textContent = view.step.kind === 'note' ? `“${view.step.lyric}” — ${view.step.hint}` : view.step.hint
    return
  }
  tutorialKicker.textContent = 'HOW TO PLAY'
  if (root.dataset.camera === 'true') {
    tutorialTitle.textContent = firstRun.copy.title
    tutorialCopy.textContent = firstRun.copy.copy
  } else {
    tutorialTitle.textContent = 'Drag to play.'
    tutorialCopy.textContent = 'Vertical movement chooses exact notes — higher is higher. Horizontal movement shifts warm to bright. Release to rest; every note echoes and fades on its own. Press Space to record a loop, or try Learn a song.'
  }
}

function replayGuide(): void {
  preview.stop()
  lesson = null
  setPerformanceControls(false)
  hintPinned = true
  if (root.dataset.camera === 'true' && firstRun.isComplete()) firstRun.restart()
  updateHintUi()
  tutorialTitle.focus()
  announce('Guide opened.')
}

function skipGuide(): void {
  preview.stop()
  hintPinned = false
  hintDismissed = true
  saveHintDismissed()
  updateHintUi()
  stage.focus()
  renderOverlay()
  announce('Guide hidden. Press How it works to bring it back.')
}

async function toggleLoops(): Promise<void> {
  loopsPlaying = !loopsPlaying
  if (loopsPlaying) {
    try { await system.audio.unlock() } catch (error) { loopsPlaying = false; updatePlayPauseUi(); announce(error instanceof Error ? error.message : 'Audio could not resume.'); return }
  }
  system.looper.setPlaying(loopsPlaying); updatePlayPauseUi()
  announce(loopsPlaying ? 'Loop playback resumed.' : 'Loops paused. The live instrument still plays.')
}

function updateFallbackControl(): void {
  if (controlsOpen || takeDialog.open) pointer.gate = false
  if (pointer.gate) {
    currentFrame = null
    handFilter.reset()
    pinchStates = pinchDetectors.map(detector => detector.lost())
    commandDetectors.forEach(detector => detector.reset())
  }
  control = mapper.fallback(pointer.x, pointer.y, pointer.gate, now())
  system.audio.update(control); plantEchoes()
  observeLesson(true, now())
  lessonTapThisFrame = false
  updateNoteReadout(); renderOverlay()
}

function releasePointer(): void { pointer.gate = false; updateFallbackControl() }

function onStageKeyDown(event: KeyboardEvent): void {
  if (controlsOpen || takeDialog.open || event.repeat) return
  const step = event.shiftKey ? 0.1 : 0.035; const key = event.key.toLowerCase()
  if (event.key === 'ArrowLeft' || key === 'a') pointer.x = Math.max(0, pointer.x - step)
  else if (event.key === 'ArrowRight' || key === 'd') pointer.x = Math.min(1, pointer.x + step)
  else if (event.key === 'ArrowUp' || key === 'w') pointer.y = Math.max(0, pointer.y - step)
  else if (event.key === 'ArrowDown' || key === 's') pointer.y = Math.min(1, pointer.y + step)
  else if (event.key === 'Enter') {
    pointer.gate = !pointer.gate
    stage.setAttribute('aria-label', `Theremin performance surface. Voice ${pointer.gate ? 'on' : 'off'}.`)
    announce(`Voice ${pointer.gate ? 'on' : 'off'}.`)
  }
  else if (event.code === 'Space') { event.preventDefault(); triggerLoopTap(); return }
  else return
  event.preventDefault(); updateFallbackControl()
}

function onGlobalKeyDown(event: KeyboardEvent): void {
  if (root.dataset.mode !== 'play' || takeDialog.open) return
  if (event.key === 'Escape' || (event.key.toLowerCase() === 'h' && !isInteractive(event.target))) {
    event.preventDefault()
    if (!event.repeat) setPerformanceControls(event.key === 'Escape' ? false : !controlsOpen)
    return
  }
  if (controlsOpen || event.repeat) return
  if (root.dataset.mode !== 'play' || event.target === stage || isInteractive(event.target)) return
  if (event.code === 'Space') { event.preventDefault(); triggerLoopTap() }
  else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(event.key) || ['w', 'a', 's', 'd'].includes(event.key.toLowerCase())) onStageKeyDown(event)
}

function setPerformanceControls(open: boolean): void {
  preview.stop()
  controlsOpen = open
  resetTrackingState()
  required('performance-controls').hidden = !open
  required('reveal-controls').setAttribute('aria-expanded', String(open))
  root.dataset.controls = String(open)
  hintPinned = false
  updateHintUi(); renderOverlay()
  if (open) required('hide-controls').focus()
  else stage.focus({ preventScroll: true })
}

// Coming back to the tab: sound and camera return on their own, no button needed.
function onPageVisible(): void {
  if (root.dataset.mode !== 'play') return
  void wakeSound()
  if (!echoClock) startEchoClock()
  if (arrivalEnabled && !cameraSession) void ensureCamera()
}

function onVisibilityChange(): void {
  if (!document.hidden) { onPageVisible(); return }
  preview.stop()
  retryCamera.hidden=false
  stopEchoClock(); hushGarden(false)
  const canceledLayer = cancelActiveLoopRecording(); releaseCamera(); system.audio.release(0.1); system.looper.setPlaying(false); loopsPlaying = false; updatePlayPauseUi()
  clearCaptureTimers(); capture.cancel(); compositor.stop(); recording = false; recordButton.disabled = false; void system.audio.suspend()
  announce(`Session paused while hidden.${canceledLayer ? ' The unfinished layer was canceled.' : ''} Camera and sound were released; press Play to resume loops.`)
}

function onPageHide(event: PageTransitionEvent): void {
  preview.stop()
  if (!event.persisted) { dispose(); return }
  retryCamera.hidden=false
  cancelActiveLoopRecording(); releaseCamera(); system.audio.release(0.1); system.looper.setPlaying(false); loopsPlaying = false; updatePlayPauseUi(); void system.audio.suspend()
}

function onPageShow(event: PageTransitionEvent): void { if (event.persisted) announce('Session restored. Press Play to resume loops; restart the camera when ready.') }

function beginCapture(): void {
  if (!recording && !silentAudio && typeof MediaRecorder !== 'undefined') setPerformanceControls(false)
  if (recording) return
  if (silentAudio || typeof MediaRecorder === 'undefined') { announce('Local recording is unavailable here. The live theremin and looper remain complete.'); return }
  recording = true; recordButton.disabled = true
  const generation = ++captureGeneration; const master = layers[0]
  const followsPhrase = Boolean(master && master.status !== 'empty' && master.durationSeconds > 0)
  const cycleMs = followsPhrase ? master!.durationSeconds * 1_000 : 2_500
  const untilBoundaryMs = followsPhrase ? ((1 - master!.loopPhase) % 1) * cycleMs : 0
  const countInMs = untilBoundaryMs + cycleMs; const takeMs = followsPhrase ? cycleMs * 4 : 10_000
  announce(followsPhrase ? 'Capture armed for the next phrase boundary, followed by one loop of count-in.' : 'Capture armed. One-bar count-in: 4 · 3 · 2 · 1')
  trackTimer(window.setTimeout(() => {
    if (!recording || generation !== captureGeneration) return
    compositor.start()
    const result = capture.start()
    if (!result.started) { compositor.stop(); recording = false; recordButton.disabled = false; announce('Combined capture is unavailable in this browser.'); return }
    const span = followsPhrase ? 'four phrase cycles' : 'a ten-second take'
    announce(result.mode === 'audio-video' ? `Recording ${span} with camera, tracking optics, loops, and audio.` : `Recording ${span} as local audio.`)
    trackTimer(window.setTimeout(() => { if (recording && generation === captureGeneration) void finishCapture() }, takeMs))
  }, countInMs))
}

async function finishCapture(): Promise<void> {
  const generation = captureGeneration
  try {
    const result = await capture.stop()
    if (generation !== captureGeneration) { URL.revokeObjectURL(result.url); return }
    if (lastRecordingUrl) URL.revokeObjectURL(lastRecordingUrl)
    lastRecordingUrl = result.url; downloadTake.href = result.url; downloadTake.download = `aether-theremin-take.${result.extension}`
    const visual = result.mode === 'audio-video'; takeVideo.hidden = !visual; takeAudio.hidden = visual
    if (visual) { takeVideo.src = result.url; takeVideo.load() } else { takeAudio.src = result.url; takeAudio.load() }
    takeCopy.textContent = visual ? 'The mirrored camera, tracking optics, loop state, and master audio were composited locally.' : 'This browser supplied a local audio-only take.'
    pointer.gate = false; resetTrackingState(); hushGarden(false);
    system.looper.setPlaying(false); loopsPlaying = false; updatePlayPauseUi();
    takeDialog.showModal(); announce('Take ready to replay or keep.')
  } catch (error) { announce(error instanceof Error ? error.message : 'Local capture could not finish.') }
  finally { compositor.stop(); if (generation === captureGeneration) { clearCaptureTimers(); recording = false; recordButton.disabled = false } }
}

function exitInstrument(): void {
  preview.stop()
  arrivalEnabled=false;handMenu.reset();retryCamera.hidden=true
  window.clearInterval(handWatchdog); handWatchdog = 0
  stopEchoClock(); hushGarden(false);
  cancelActiveLoopRecording(); releaseCamera(); system.audio.release(); system.looper.setPlaying(false); loopsPlaying = false; updatePlayPauseUi(); void system.audio.suspend()
  clearCaptureTimers(); capture.cancel(); compositor.stop(); root.dataset.mode = 'intro'; recording = false; recordButton.disabled = false; updateSoundUi()
  announce('Session stopped. Camera and sound resources were released.'); startCameraButton.focus()
}

function releaseCamera(): void {
  handMenu.reset()
  cameraGeneration += 1; cancelAnimationFrame(visionFrame); visionFrame = 0; vision?.dispose(); vision = null; stopCamera(cameraSession); cameraSession = null
  if (!video.paused) video.pause()
  video.srcObject = null; root.dataset.camera = 'false';  resetTrackingState(); renderOverlay()
}

function handleTrackingError(message: string): void {
  resetTrackingState()
  renderOverlay()
  announce(`Tracking: ${message} The live voice was safely released; pointer, touch, and keyboard controls remain available.`)
}

function resetTrackingState(): void {
  pointer.gate = false
  currentFrame = null
  handFilter.reset()
  
  pinchStates = pinchDetectors.map((detector) => detector.lost())
  commandDetectors.forEach((detector) => detector.reset())
  commandRings = []
  mapper.reset()
  control = mapper.update([], now())
  system.audio.update(control)
  system.audio.release(0.1)
  updateNoteReadout()
}

function cancelActiveLoopRecording(): boolean {
  const recordingLayer = layers.find((layer) => layer.status === 'recording')
  if (!recordingLayer) return false
  system.looper.clear(recordingLayer.index)
  return true
}

function videoFrame(): void {
  visionFrame = 0
  if (!vision || !cameraSession) return
  vision.submit(video, now(), true); visionFrame = requestAnimationFrame(videoFrame)
}

function startHandWatchdog(): void {
  window.clearInterval(handWatchdog)
  handWatchdog = window.setInterval(() => {
    if (lastHandReceivedAt > 0 && now() - lastHandReceivedAt > 300) {
      handMenu.reset()
      if (currentFrame && !pointer.gate) { resetTrackingState(); renderOverlay() }
    }
  }, 100)
}

function renderOverlay(): void { overlay.render(currentFrame, control, pinchStates, layers, overlayExtras()) }

function updateNoteReadout(): void {
  const primary = control.voices.find((voice) => voice.gate) ?? control.voices[0]
  noteReadout.textContent = noteName(primary.targetMidi)
  frequencyReadout.textContent = `${Math.round(primary.pitchHz)} Hz`
}

function drawWaveform(canvas: HTMLCanvasElement, layer: LoopLayerState): void {
  const context = canvas.getContext('2d'); if (!context) return
  const bounds = canvas.getBoundingClientRect(); canvas.width = Math.max(1, Math.round(bounds.width * (window.devicePixelRatio || 1))); canvas.height = Math.max(1, Math.round(bounds.height * (window.devicePixelRatio || 1)))
  context.clearRect(0, 0, canvas.width, canvas.height)
  const peaks = layer.waveformPeaks.length > 0 ? layer.waveformPeaks : Array.from({ length: 32 }, (_, index) => layer.status === 'empty' ? 0.05 : 0.12 + Math.sin(index * 1.7) ** 2 * 0.16)
  context.strokeStyle = layer.index === 0 ? '#f4bd62' : '#55d5e8'; context.globalAlpha = layer.status === 'muted' ? 0.3 : 0.84; context.beginPath()
  peaks.forEach((peak, index) => { const x = index / Math.max(1, peaks.length - 1) * canvas.width; const height = Math.max(1, peak * canvas.height * 0.82); context.moveTo(x, (canvas.height - height) / 2); context.lineTo(x, (canvas.height + height) / 2) })
  context.stroke()
}

function layerLabel(layer: LoopLayerState): string {
  if (layer.status === 'empty') return layer.index === 0 ? 'READY · DEFINE PHRASE' : 'WAITING'
  if (layer.status === 'recording') return 'RECORDING · PINCH TO CLOSE'
  if (layer.status === 'muted') return 'MUTED'
  return `${layer.durationSeconds.toFixed(1)}S · PLAYING`
}

function normalizedPinchDistance(landmarks: HandLandmark[]): number {
  if (landmarks.length < 21) return 1
  const palmWidth = Math.max(0.001, distance(landmarks[5]!, landmarks[17]!))
  return distance(landmarks[4]!, landmarks[8]!) / palmWidth
}

function defaultCalibration(handedness: Handedness): PinchCalibration {
  return { handedness, openMedian: 1.05, closedMedian: 0.2, contactThreshold: 0.38, releaseThreshold: 0.62, valid: true, calibratedAt: 0 }
}
function readPreset(): VoicePreset { try { const value = localStorage.getItem('aether-voice-v5') as VoicePreset | null; return value && VOICE_PRESETS.includes(value) ? value : 'harp' } catch { return 'harp' } }
function readEchoEnabled(): boolean { try { return localStorage.getItem('aether-echo-v5') !== 'off' } catch { return true } }
function readMasterLevel(): number { try { const raw = localStorage.getItem('aether-master-level-v3'); if (raw === null) return 0.78; const value = Number(raw); return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0.78 } catch { return 0.78 } }
function readHintDismissed(): boolean { try { return localStorage.getItem('aether-hint-v4') === 'dismissed' } catch { return false } }
function readFirstRunComplete(): boolean { try { return localStorage.getItem('aether-firstrun-v5') === 'complete' } catch { return false } }
function saveFirstRunComplete(): void { try { localStorage.setItem('aether-firstrun-v5', 'complete') } catch { /* optional */ } }
function saveHintDismissed(): void { try { localStorage.setItem('aether-hint-v4', 'dismissed') } catch { /* optional */ } }
function distance(a: HandLandmark, b: HandLandmark): number { return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) }
function voiceLabel(value: VoicePreset): string {
  const labels: Record<VoicePreset, string> = { harp: 'Harp', 'music-box': 'Music Box', piano: 'Piano', 'glass-bow': 'Glass', 'breath-choir': 'Choir', aurora: 'Aurora' }
  return labels[value]
}
function noteName(midi: number): string { const names = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B']; return `${names[(midi % 12 + 12) % 12]}${Math.floor(midi / 12) - 1}` }
function now(): number { return typeof performance === 'undefined' ? Date.now() : performance.now() }
function isInteractive(target: EventTarget | null): boolean { return target instanceof Element && target.closest('button,a,input,select,textarea,dialog,audio,video,[contenteditable=true]') !== null }
function pointFromEvent(event: PointerEvent): { x: number; y: number } { const bounds = stage.getBoundingClientRect(); return { x: clamp01((event.clientX - bounds.left) / Math.max(1, bounds.width)), y: clamp01((event.clientY - bounds.top) / Math.max(1, bounds.height)) } }
function clamp01(value: number): number { return Math.max(0, Math.min(1, value)) }
function announce(message: string): void { if (status.textContent !== message) status.textContent = message }
function trackTimer(timer: number): void { captureTimers.add(timer) }
function clearCaptureTimers(): void { captureGeneration += 1; for (const timer of captureTimers) window.clearTimeout(timer); captureTimers.clear() }
function updatePlayPauseUi(): void { playPauseButton.setAttribute('aria-label', loopsPlaying ? 'Pause loops' : 'Play loops'); playPauseButton.innerHTML = loopsPlaying ? '<span aria-hidden="true">Ⅱ</span><span>Pause</span>' : '<span aria-hidden="true">▶</span><span>Play</span>' }
function emptyLayers(): LoopLayerState[] { return Array.from({ length: 4 }, (_, index) => ({ index, status: 'empty', loopPhase: 0, durationSeconds: 0, gain: 1, waveformPeaks: [], recordStartPhase: 0 })) }
function required<T extends HTMLElement>(id: string): T { const element = document.getElementById(id); if (!element) throw new Error(`Missing required element #${id}`); return element as T }

function dispose(): void {
  preview.stop()
  arrivalEnabled=false;handMenu.dispose()
  window.clearInterval(handWatchdog)
  stopEchoClock(); releaseCamera(); clearCaptureTimers(); capture.cancel(); compositor.stop(); overlay.dispose();  system.looper.dispose(); system.audio.dispose()
  if (lastRecordingUrl) URL.revokeObjectURL(lastRecordingUrl)
}

// Camera permission and tracking do not wait for audio activation.
void ensureCamera()
